import React, { useState, useEffect } from 'react';
import { 
  Printer, 
  FileText, 
  Check, 
  ArrowRight, 
  Download,
  FileDown,
  Users,
  ScrollText,
  RefreshCw,
  Info,
  Eye,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { useLifely } from '../context/LifelyContext';
import { toast } from 'sonner';

// Printable Bundle Component
const PrintableBundle = ({ 
  selectedMapsList, 
  includeWillStatus, 
  includeContacts, 
  includeWishes,
  detailLevel,
  willStatus, 
  contacts, 
  getMapNotes, 
  getEntriesForMap, 
  getMapWishes,
  onBack,
  onPrint 
}) => {
  return (
    <div className="print-bundle" data-testid="printable-bundle">
      <div className="no-print mb-8 flex items-center gap-4">
        <Button onClick={onPrint} data-testid="print-btn">
          <Printer className="w-4 h-4 mr-2" />
          Print / Save as PDF
        </Button>
        <Button variant="ghost" onClick={onBack} data-testid="back-to-hub-btn">
          Back to Export Hub
        </Button>
      </div>

      <div className="space-y-8">
        <header className="border-b-2 border-border pb-6">
          <h1 className="text-2xl font-heading font-semibold">Lifely — Personal Information Summary</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
            {selectedMapsList.length > 0 && <p>Maps: {selectedMapsList.map(m => m.name).join(', ')}</p>}
            {includeWillStatus && <p>Includes: Will & Legal Status</p>}
            {includeContacts && <p>Includes: Important Contacts</p>}
          </div>
        </header>

        {includeWillStatus && willStatus && (
          <section className="page-break-before" data-testid="print-will-status">
            <h2 className="text-xl font-heading font-medium mb-4 pb-2 border-b border-border flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Will & Legal Status
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Will Status</p>
                  <p className="font-medium capitalize">{willStatus.willExists || 'Not specified'}</p>
                </div>
                {willStatus.willExists === 'yes' && (
                  <div>
                    <p className="text-sm text-muted-foreground">Held With</p>
                    <p className="font-medium">{willStatus.firmName || willStatus.heldWith || '—'}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {includeContacts && contacts && contacts.length > 0 && (
          <section className="page-break-before" data-testid="print-contacts">
            <h2 className="text-xl font-heading font-medium mb-4 pb-2 border-b border-border flex items-center gap-2">
              <Users className="w-5 h-5" />
              Important Contacts ({contacts.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="p-4 border border-border rounded-lg">
                  <p className="font-medium">{contact.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{contact.category}</p>
                  {detailLevel === 'full' && contact.email && <p className="text-sm">{contact.email}</p>}
                  {detailLevel === 'full' && contact.phone && <p className="text-sm">{contact.phone}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {selectedMapsList.map((map) => {
          const notes = getMapNotes(map.id);
          const entries = getEntriesForMap(map.id);
          const wishes = includeWishes ? getMapWishes(map.id) : null;
          
          return (
            <section key={map.id} className="page-break-before" data-testid={`print-map-${map.id}`}>
              <h2 className="text-xl font-heading font-medium mb-4 pb-2 border-b border-border">{map.name}</h2>
              
              {notes && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Notes & Intent</h3>
                  <p className="text-sm leading-relaxed">{notes}</p>
                </div>
              )}

              {wishes && wishes.notes && (
                <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Your Wishes (Non-binding)</h3>
                  <p className="text-sm leading-relaxed">{wishes.notes}</p>
                </div>
              )}

              {entries.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground">Entries ({entries.length})</h3>
                  {entries.map((entry) => (
                    <div key={entry.id} className="p-4 border border-border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{entry.label}</p>
                          <p className="text-sm text-muted-foreground">{entry.location}</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-muted rounded">
                          {entry.priority.charAt(0).toUpperCase() + entry.priority.slice(1)}
                        </span>
                      </div>
                      {detailLevel === 'full' && entry.notes && <p className="text-sm mt-2">{entry.notes}</p>}
                      {detailLevel === 'full' && entry.firstAction && (
                        <p className="text-sm mt-2 text-primary"><strong>First action:</strong> {entry.firstAction}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No entries in this map.</p>
              )}
            </section>
          );
        })}

        <footer className="pt-8 border-t border-border text-sm text-muted-foreground">
          <p>This document was generated from Lifely for informational purposes only.</p>
          <p>It does not replace legal documents and should not be considered legal advice.</p>
        </footer>
      </div>
    </div>
  );
};

const PrintExport = () => {
  const { 
    maps, 
    getEntriesForMap, 
    getMapNotes, 
    getMapWishes,
    willStatus: contextWillStatus, 
    contacts: contextContacts, 
    continuityConfig: contextContinuityConfig,
    fetchMaps,
    fetchContacts,
    fetchWillStatus,
    fetchContinuity,
    isAuthenticated,
    loading
  } = useLifely();

  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch all data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([
            fetchMaps(),
            fetchContacts(),
            fetchWillStatus(),
            fetchContinuity()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load export data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchMaps, fetchContacts, fetchWillStatus, fetchContinuity]);

  // Use context data with fallbacks
  const willStatus = contextWillStatus || {};
  const contacts = contextContacts || [];
  const continuityConfig = contextContinuityConfig || {};
  
  const [selectedMaps, setSelectedMaps] = useState({});
  const [includeWillStatus, setIncludeWillStatus] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  const [includeContinuity, setIncludeContinuity] = useState(false);
  const [includeWishes, setIncludeWishes] = useState(true);
  const [exportFormat, setExportFormat] = useState('printable');
  const [detailLevel, setDetailLevel] = useState('full');
  const [showPrintable, setShowPrintable] = useState(false);

  const handleToggleMap = (mapId) => {
    setSelectedMaps(prev => ({ ...prev, [mapId]: !prev[mapId] }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.keys(selectedMaps).length === maps.length && Object.values(selectedMaps).every(v => v);
    if (allSelected) {
      setSelectedMaps({});
    } else {
      const newSelected = {};
      maps.forEach(m => { newSelected[m.id] = true; });
      setSelectedMaps(newSelected);
    }
  };

  const selectedCount = Object.values(selectedMaps).filter(Boolean).length;
  const hasAnySelection = selectedCount > 0 || includeWillStatus || includeContacts || includeContinuity;

  const handleGenerateBundle = () => {
    if (!hasAnySelection) {
      toast.error('Please select at least one item to include');
      return;
    }
    setShowPrintable(true);
  };

  const handlePrint = () => window.print();

  const handleExportJSON = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      maps: maps.filter(m => selectedMaps[m.id]).map(map => ({
        ...map,
        entries: getEntriesForMap(map.id),
        notes: getMapNotes(map.id),
        wishes: includeWishes ? getMapWishes(map.id) : null,
      })),
      willStatus: includeWillStatus ? willStatus : null,
      contacts: includeContacts ? contacts : null,
      continuity: includeContinuity ? { 
        isConfigured: continuityConfig?.isConfigured || false,
        checkInFrequency: continuityConfig?.checkInFrequency || 0,
        nomineesCount: continuityConfig?.nominees?.length || 0,
      } : null,
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifely-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON export downloaded successfully');
  };

  // Show loading state
  const isLoading = loading.maps || loading.contacts || loading.willStatus || loading.continuity;
  if (isLoading && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (showPrintable) {
    const selectedMapsList = maps.filter(m => selectedMaps[m.id]);
    return (
      <PrintableBundle
        selectedMapsList={selectedMapsList}
        includeWillStatus={includeWillStatus}
        includeContacts={includeContacts}
        includeWishes={includeWishes}
        detailLevel={detailLevel}
        willStatus={willStatus}
        contacts={contacts}
        getMapNotes={getMapNotes}
        getEntriesForMap={getEntriesForMap}
        getMapWishes={getMapWishes}
        onBack={() => setShowPrintable(false)}
        onPrint={handlePrint}
      />
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="print-export-page">
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">Export Hub</h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          Create printable summaries or export your data. Choose what to include and how much detail you need.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Maps Selection */}
          <Card className="border border-border" data-testid="map-selection-card">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base font-medium font-body">Maps</CardTitle>
                <CardDescription className="text-xs">Select which maps to include</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={handleSelectAll} data-testid="select-all-btn">
                {selectedCount === maps.length ? 'Deselect All' : 'Select All'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {maps.map((map) => {
                  const entriesCount = getEntriesForMap(map.id).length;
                  return (
                    <div 
                      key={map.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedMaps[map.id] ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'
                      }`}
                      onClick={() => handleToggleMap(map.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={`map-${map.id}`}
                          checked={selectedMaps[map.id] || false}
                          onCheckedChange={() => handleToggleMap(map.id)}
                          data-testid={`select-map-${map.id}`}
                        />
                        <Label htmlFor={`map-${map.id}`} className="cursor-pointer">
                          <p className="font-medium text-sm">{map.name}</p>
                          <p className="text-xs text-muted-foreground">{entriesCount} entries</p>
                        </Label>
                      </div>
                      {selectedMaps[map.id] && <Check className="w-4 h-4 text-primary" />}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Additional Sections */}
          <Card className="border border-border" data-testid="additional-sections-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium font-body">Additional Sections</CardTitle>
              <CardDescription className="text-xs">Include other important information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <ScrollText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Will & Legal Status</p>
                    <p className="text-xs text-muted-foreground">Will details, executors, POA info</p>
                  </div>
                </div>
                <Switch checked={includeWillStatus} onCheckedChange={setIncludeWillStatus} data-testid="include-will-status" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Important Contacts</p>
                    <p className="text-xs text-muted-foreground">{contacts.length} contacts stored</p>
                  </div>
                </div>
                <Switch checked={includeContacts} onCheckedChange={setIncludeContacts} data-testid="include-contacts" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Continuity Settings</p>
                    <p className="text-xs text-muted-foreground">Check-in schedule (no sensitive data)</p>
                  </div>
                </div>
                <Switch checked={includeContinuity} onCheckedChange={setIncludeContinuity} data-testid="include-continuity" />
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Include Your Wishes</p>
                    <p className="text-xs text-muted-foreground">Non-binding intent notes on maps and entries</p>
                  </div>
                </div>
                <Switch checked={includeWishes} onCheckedChange={setIncludeWishes} data-testid="include-wishes" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <Card className="border border-border" data-testid="export-format-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium font-body">Export Format</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <RadioGroup value={exportFormat} onValueChange={setExportFormat}>
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    exportFormat === 'printable' ? 'border-primary/40 bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setExportFormat('printable')}
                >
                  <RadioGroupItem value="printable" id="format-printable" className="mt-0.5" />
                  <Label htmlFor="format-printable" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">Printable View</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Clean layout for printing or PDF</p>
                  </Label>
                </div>
                
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    exportFormat === 'json' ? 'border-primary/40 bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setExportFormat('json')}
                >
                  <RadioGroupItem value="json" id="format-json" className="mt-0.5" />
                  <Label htmlFor="format-json" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <FileDown className="w-4 h-4 text-accent" />
                      <span className="font-medium text-sm">JSON Data</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Machine-readable backup</p>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          <Card className="border border-border" data-testid="detail-level-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium font-body">Detail Level</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <RadioGroup value={detailLevel} onValueChange={setDetailLevel}>
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    detailLevel === 'full' ? 'border-primary/40 bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setDetailLevel('full')}
                >
                  <RadioGroupItem value="full" id="detail-full" className="mt-0.5" />
                  <Label htmlFor="detail-full" className="cursor-pointer">
                    <span className="font-medium text-sm">Full Details</span>
                    <p className="text-xs text-muted-foreground">All notes, contact info, first actions</p>
                  </Label>
                </div>
                
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    detailLevel === 'summary' ? 'border-primary/40 bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setDetailLevel('summary')}
                >
                  <RadioGroupItem value="summary" id="detail-summary" className="mt-0.5" />
                  <Label htmlFor="detail-summary" className="cursor-pointer">
                    <span className="font-medium text-sm">Summary Only</span>
                    <p className="text-xs text-muted-foreground">Names, locations, priorities</p>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          <Card className="border border-border bg-muted/30">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Selected:</span>
                <span className="font-medium">
                  {selectedCount} maps{includeWillStatus && ' + Will'}{includeContacts && ' + Contacts'}
                </span>
              </div>
              
              {exportFormat === 'printable' ? (
                <Button onClick={handleGenerateBundle} disabled={!hasAnySelection} className="w-full" data-testid="generate-bundle-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview & Print
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleExportJSON} disabled={!hasAnySelection} className="w-full" variant="secondary" data-testid="export-json-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download JSON
                </Button>
              )}
            </CardContent>
          </Card>

          <div className="bg-muted/50 rounded-xl p-4 border border-border">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Tip:</span> Use Printable View to create a PDF via your browser&apos;s Print function (Ctrl/Cmd + P). JSON export is useful for backups.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintExport;
